import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  TextInput,
  Select,
  Spinner,
  Table,
  TableHead,
  TableHeadCell,
  TableRow,
  TableCell,
  TableBody,
  Badge,
} from "flowbite-react";
import PropertyCardModal from "../components/modal/PropertyCardModal";
import {
  auctionPropertiesActions,
  auctionPropertiesThunks,
  auctionsThunks,
  countiesThunks,
} from "../app/store";
import { LuLandPlot, LuMapPinHouse } from "react-icons/lu";
import { FiEye, FiCalendar, FiExternalLink } from "react-icons/fi";
import { MdOutlineGavel } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import { HiOutlineStar, HiStar } from "react-icons/hi2";

// ✅ adiciona "NONE" (sem rating)
const RATING_FILTER_OPTIONS = [
  { value: "", label: "Todas" },
  { value: "NONE", label: "Sem rating (pendentes)" },
  { value: "A", label: "Rating A" },
  { value: "B", label: "Rating B" },
  { value: "C", label: "Rating C" },
  { value: "REDEEMED", label: "Rating Redeemed" },
];

const AUCTION_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pendente" },
  { value: "IN_PROGRESS", label: "Em andamento" },
  { value: "WAITING_ADMIN", label: "Aguardando Admin" },
  { value: "APPROVED", label: "Aprovado" },
  { value: "ABANDONED", label: "Abandonado" },
];

export default function AuctionPropertiesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auctionId } = useParams();

  const auction = useSelector((s) => {
    const fromById = s.auctions?.byId?.[auctionId] || null;
    const fromCurrent =
      s.auctions?.current?.data?.id === auctionId
        ? s.auctions.current.data
        : null;
    return fromById || fromCurrent;
  });

  const { items, status, error, count } = useSelector(
    (s) => s.auctionProperties,
  );
  const authUser = useSelector((s) => s.auth?.user || null);
  const isAdmin = authUser?.role === "ADMIN";
  const isVA = authUser?.role === "VA";

  const isLoading = status === "loading";

  const [q, setQ] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");

  // modal create
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    parcel_number: "",
    owner_name: "",
    property_address: "",
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({
    parcel_number: "",
    owner_name: "",
    property_address: "",
  });
  const [cardOpen, setCardOpen] = useState(false);
  const [cardPropertyId, setCardPropertyId] = useState(null);
  const countiesById = useSelector((s) => s.counties.byId);
  const RESEARCH_STATUS_LABELS = {
    PENDING: "Pendente",
    READY: "Pronto p/ VA",
    IN_PROGRESS: "Em andamento",
    WAITING_ADMIN: "Aguardando Admin",
    APPROVED: "Aprovado",
    ABANDONED: "Abandonado",
  };
  const [auctionStatus, setAuctionStatus] = useState(
    auction?.research_status || "",
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const cardFromUrl = searchParams.get("card");
  const progress = useMemo(() => {
    const total = Array.isArray(items) ? items.length : 0;
    const done = Array.isArray(items)
      ? items.filter((x) => !!x.rating).length
      : 0;
    const pending = total - done;

    return { total, done, pending };
  }, [items]);

  const auctionExternalUrl = useMemo(() => {
    if (auction?.list_source_url) return auction.list_source_url;

    const county = auction?.county_id
      ? countiesById?.[auction.county_id]
      : null;
    const auctionSite = county?.links?.find(
      (l) => l.link_type === "AUCTION_SITE",
    );
    return auctionSite?.url || null;
  }, [auction?.list_source_url, auction?.county_id, countiesById]);

  const countyName = useMemo(() => {
    return auction?.county_id
      ? countiesById?.[auction.county_id]?.name || auction.county_id
      : auctionId;
  }, [auction?.county_id, countiesById, auctionId]);

  const startsAtLabel = useMemo(() => {
    return auction?.starts_at
      ? new Date(auction.starts_at).toLocaleString()
      : "Sem data";
  }, [auction?.starts_at]);

  const dueAtLabel = useMemo(() => {
    return auction?.research_due_at
      ? new Date(auction.research_due_at).toLocaleString()
      : "Sem deadline";
  }, [auction?.research_due_at]);

  function dueBadgeColor(dueAtIso) {
    if (!dueAtIso) return "gray";
    const now = new Date();
    const due = new Date(dueAtIso);
    if (Number.isNaN(due.getTime())) return "gray";

    const diffMs = due.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffMs < 0) return "failure"; // atrasado
    if (diffDays <= 1) return "warning"; // vence em até 1 dia
    if (diffDays <= 3) return "info"; // vence em até 3 dias
    return "success"; // ok
  }

  useEffect(() => {
    if (auction?.research_status) {
      setAuctionStatus(auction.research_status);
    }
  }, [auction?.research_status]);

  const isSubmittable = useMemo(() => {
    const hasItems = progress.total > 0;
    const noPending = progress.pending === 0;

    // evita mostrar em status finais
    const blockedStatuses = ["WAITING_ADMIN", "APPROVED", "ABANDONED"];
    const statusOk = !blockedStatuses.includes(auction?.research_status);

    return hasItems && noPending && statusOk;
  }, [progress, auction?.research_status]);

  async function onSubmitAuction() {
    if (!auctionId) return;
    if (progress.pending !== 0) return;

    const res = await dispatch(
      auctionsThunks.updateOne({
        id: auctionId,
        payload: { research_status: "WAITING_ADMIN" },
      }),
    );

    // se o thunk foi bem-sucedido
    if (!res?.error) {
      navigate("/app/auctions");
    }
  }

  function openCard(row) {
    const next = new URLSearchParams(searchParams);
    next.set("card", row.id);
    setSearchParams(next, { replace: false });

    setCardPropertyId(row.id);
    setCardOpen(true);
  }

  function closeCard() {
    const next = new URLSearchParams(searchParams);
    next.delete("card");
    setSearchParams(next, { replace: false });

    setCardOpen(false);
    setCardPropertyId(null);
  }

  async function onChangeAuctionStatus(nextStatus) {
    if (!auctionId) return;
    if (nextStatus === auction?.research_status) return;

    const res = await dispatch(
      auctionsThunks.updateOne({
        id: auctionId,
        payload: { research_status: nextStatus },
      }),
    );

    if (!res?.error) {
      setAuctionStatus(nextStatus);
    }
  }

  useEffect(() => {
    if (!cardFromUrl) return;
    setCardPropertyId(cardFromUrl);
    setCardOpen(true);
  }, [cardFromUrl]);

  useEffect(() => {
    dispatch(countiesThunks.list({ q: "", limit: 9999, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (!auctionId) return;
    dispatch(auctionsThunks.getById(auctionId));
    dispatch(
      auctionPropertiesThunks.list({
        auction_id: auctionId,
        q: "",
        limit: 5000,
        offset: 0,
      }),
    );
  }, [dispatch, auctionId]);

  function buildListParams() {
    return {
      auction_id: auctionId,
      q: q || "",
      limit: 5000,
      offset: 0,
    };
  }

  function openCard(row) {
    const next = new URLSearchParams(searchParams);
    next.set("card", row.id);
    setSearchParams(next, { replace: false });

    setCardPropertyId(row.id);
    setCardOpen(true);
  }

  function closeCard() {
    const next = new URLSearchParams(searchParams);
    next.delete("card");
    setSearchParams(next, { replace: false });

    setCardOpen(false);
    setCardPropertyId(null);
  }

  function onSearch() {
    dispatch(auctionPropertiesActions.clearError());
    dispatch(auctionPropertiesThunks.list(buildListParams()));
  }

  function openCreate() {
    setCreateForm({ parcel_number: "", owner_name: "", property_address: "" });
    setCreateOpen(true);
  }

  function closeCreate() {
    setCreateOpen(false);
  }

  function statusBadgeColor(status) {
    switch (status) {
      case "PENDING":
        return "warning";
      case "READY":
        return "info";
      case "IN_PROGRESS":
        return "purple";
      case "WAITING_ADMIN":
        return "warning";
      case "APPROVED":
        return "success";
      case "ABANDONED":
        return "failure";
      default:
        return "gray";
    }
  }

  async function onCreate() {
    if (!createForm.parcel_number?.trim()) return;

    await dispatch(
      auctionPropertiesThunks.createOne({
        auction_id: auctionId,
        parcel_number: createForm.parcel_number.trim(),
        owner_name: createForm.owner_name?.trim() || null,
        property_address: createForm.property_address?.trim() || null,
      }),
    );

    dispatch(auctionPropertiesThunks.list(buildListParams()));
    closeCreate();
  }

  function openResearch(row) {
    navigate(`/app/auctions/${auctionId}/properties/${row.id}/research`);
  }

  function openEdit(row) {
    setEditingRow(row);
    setEditForm({
      parcel_number: row.parcel_number || "",
      owner_name: row.owner_name || "",
      property_address: row.property_address || "",
    });
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
    setEditingRow(null);
  }

  async function onUpdateHeader() {
    if (!editingRow?.id) return;

    await dispatch(
      auctionPropertiesThunks.updateOne({
        id: editingRow.id,
        payload: {
          parcel_number: editForm.parcel_number.trim(),
          owner_name: editForm.owner_name?.trim() || null,
          property_address: editForm.property_address?.trim() || null,
        },
      }),
    );

    dispatch(auctionPropertiesThunks.list(buildListParams()));
    closeEdit();
  }

  // ✅ filtro local para "Sem rating"
  const filteredItems = useMemo(() => {
    if (!items?.length) return [];

    // Todas
    if (!ratingFilter) return items;

    // Sem rating (pendentes)
    if (ratingFilter === "NONE") return items.filter((x) => !x.rating);

    // Rating específico
    return items.filter((x) => x.rating === ratingFilter);
  }, [items, ratingFilter]);

  function renderStatusCell(r) {
    const done = !!r.rating; // se tem A/B/C, acabou
    return (
      <div className="flex items-center gap-2">
        {done ? (
          <>
            <span title="Finalizado" aria-label="Finalizado">
              ✅
            </span>
            <Badge color="success">Finalizado</Badge>
          </>
        ) : (
          <>
            <span title="Pendente" aria-label="Pendente">
              ⏳
            </span>
            <Badge color="warning">Pendente</Badge>
          </>
        )}
      </div>
    );
  }

  async function toggleFavorite(row) {
    if (!row?.id) return;

    await dispatch(
      auctionPropertiesThunks.updateOne({
        id: row.id,
        payload: { is_favorite: !row.is_favorite },
      }),
    );

    // se o slice não atualizar o item automaticamente,
    // você pode refetch aqui (mas eu recomendo ajustar o reducer)
    // dispatch(auctionPropertiesThunks.list(buildListParams()));
  }

  function renderRatingCell(r) {
    if (!r.rating) return <span className="text-gray-500">—</span>;
    const color =
      r.rating === "A" ? "success" : r.rating === "B" ? "info" : r.rating === "REDEEMED" ? "warning" : "failure";
    return <Badge color={color}>Rating {r.rating}</Badge>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Propriedades do Auction
          </h1>

          {/* Card clicável (condado + data + status) */}
          {auctionExternalUrl ? (
            <a
              href={auctionExternalUrl}
              target="_blank"
              rel="noreferrer noopener"
              title="Abrir site do leilão em nova aba"
              aria-label="Abrir site do leilão em nova aba"
              className="
        group block rounded-xl border border-gray-200 dark:border-gray-800
        bg-white dark:bg-gray-900 p-4
        hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700
        transition
      "
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 text-gray-700 dark:text-gray-200">
                    <MdOutlineGavel size={22} />
                  </div>

                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        {countyName}
                      </span>

                      <span className="text-gray-400">•</span>

                      <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <FiCalendar />
                        {startsAtLabel}
                      </span>

                      <span className="text-gray-400">•</span>

                      <Badge color={dueBadgeColor(auction?.research_due_at)}>
                        Due: {dueAtLabel}
                      </Badge>

                      <span className="text-gray-400">•</span>

                      <Badge color={statusBadgeColor(auction?.research_status)}>
                        {RESEARCH_STATUS_LABELS[auction?.research_status] ||
                          auction?.research_status ||
                          "—"}
                      </Badge>
                    </div>

                    <div className="text-sm text-gray-500">
                      {count || 0} registro(s) •{" "}
                      <span className="text-green-700 dark:text-green-400">
                        Finalizadas: {progress.done}
                      </span>{" "}
                      •{" "}
                      <span className="text-yellow-700 dark:text-yellow-400">
                        Pendentes: {progress.pending}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Hint visual de "link externo" sem texto */}
                <div className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition">
                  <FiExternalLink />
                </div>
              </div>
            </a>
          ) : (
            // fallback quando não tem link
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-gray-700 dark:text-gray-200">
                  <MdOutlineGavel size={22} />
                </div>

                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {countyName}
                    </span>

                    <span className="text-gray-400">•</span>

                    <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <FiCalendar />
                      {startsAtLabel}
                    </span>

                    <span className="text-gray-400">•</span>

                    <Badge color={statusBadgeColor(auction?.research_status)}>
                      {RESEARCH_STATUS_LABELS[auction?.research_status] ||
                        auction?.research_status ||
                        "—"}
                    </Badge>
                  </div>

                  <div className="text-sm text-gray-500">
                    {count || 0} registro(s) • Finalizadas: {progress.done} •
                    Pendentes: {progress.pending}
                  </div>

                  <div className="text-xs text-gray-400">
                    Link do leilão não cadastrado.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button color="gray" onClick={() => navigate("/app/auctions")}>
            Voltar
          </Button>

          {isAdmin && (
            <Button className="text-slate-600" onClick={openCreate}>
              Nova Propriedade
            </Button>
          )}

          {isSubmittable && (
            <Button className="bg-green-700" onClick={onSubmitAuction}>
              Submit p/ Admin
            </Button>
          )}
          {isAdmin && (
            <Select
              value={auctionStatus}
              onChange={(e) => onChangeAuctionStatus(e.target.value)}
            >
              {AUCTION_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>

      {error && (
        <Alert
          color="failure"
          onDismiss={() => dispatch(auctionPropertiesActions.clearError())}
        >
          {error}
        </Alert>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <TextInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por parcel, owner, address..."
        />

        <Select
          value={ratingFilter}
          onChange={(e) => setRatingFilter(e.target.value)}
        >
          {RATING_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value || "ALL"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>

        <Button color="gray" onClick={onSearch} disabled={isLoading}>
          Buscar
        </Button>
      </div>

      <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-6 flex items-center gap-3">
            <Spinner />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Carregando...
            </span>
          </div>
        ) : (
          <Table hoverable>
            <TableHead>
              <TableHeadCell>Ações</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Rating</TableHeadCell>
              <TableHeadCell>Parcel</TableHeadCell>
              <TableHeadCell>Owner</TableHeadCell>
              <TableHeadCell>Address</TableHeadCell>
            </TableHead>

            <TableBody className="divide-y">
              {filteredItems?.length ? (
                filteredItems.map((r) => (
                  <TableRow
                    key={r.id}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleFavorite(r)}
                          title={
                            r.is_favorite
                              ? "Remover dos favoritos"
                              : "Favoritar"
                          }
                          className={`transition ${
                            r.is_favorite
                              ? "text-yellow-400 hover:text-yellow-500"
                              : "text-gray-400 hover:text-yellow-400"
                          }`}
                          type="button"
                        >
                          {r.is_favorite ? (
                            <HiStar size={18} />
                          ) : (
                            <HiOutlineStar size={18} />
                          )}
                        </button>
                        <button
                          onClick={() => openResearch(r)}
                          title="Abrir pesquisa completa / editar tudo"
                          className="text-gray-500 hover:text-green-600 transition"
                          type="button"
                        >
                          <LuLandPlot size={18} />
                        </button>

                        <button
                          onClick={() => openEdit(r)}
                          title="Editar cabeçalho (parcel, endereço e owner)"
                          className="text-gray-500 hover:text-blue-600 transition"
                          type="button"
                        >
                          <LuMapPinHouse size={18} />
                        </button>

                        <button
                          onClick={() => openCard(r)}
                          title="Ver resultado da pesquisa (Property Card)"
                          className="text-gray-500 hover:text-purple-600 transition"
                          type="button"
                        >
                          <FiEye size={18} />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>{renderStatusCell(r)}</TableCell>
                    <TableCell>{renderRatingCell(r)}</TableCell>

                    <TableCell className="font-medium text-gray-900 dark:text-white">
                      {r.parcel_number || "—"}
                    </TableCell>

                    <TableCell>{r.owner_name || "—"}</TableCell>
                    <TableCell>{r.property_address || "—"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-6 text-gray-500"
                  >
                    Nenhuma propriedade encontrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create Modal */}
      <Modal show={createOpen} onClose={closeCreate}>
        <ModalHeader>Nova Propriedade</ModalHeader>
        <ModalBody>
          <div className="space-y-3">
            <div>
              <Label value="Parcel Number *" />
              <TextInput
                value={createForm.parcel_number}
                onChange={(e) =>
                  setCreateForm((s) => ({
                    ...s,
                    parcel_number: e.target.value,
                  }))
                }
                placeholder="Parcel ID:"
              />
            </div>

            <div>
              <Label value="Owner (opcional)" />
              <TextInput
                value={createForm.owner_name}
                onChange={(e) =>
                  setCreateForm((s) => ({ ...s, owner_name: e.target.value }))
                }
                placeholder="Owner"
              />
            </div>

            <div>
              <Label value="Address (opcional)" />
              <TextInput
                value={createForm.property_address}
                onChange={(e) =>
                  setCreateForm((s) => ({
                    ...s,
                    property_address: e.target.value,
                  }))
                }
                placeholder="Address"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={closeCreate}>
            Cancelar
          </Button>
          <Button
            className="text-slate-800"
            onClick={onCreate}
            disabled={!createForm.parcel_number?.trim()}
          >
            Criar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Header Modal */}
      <Modal show={editOpen} onClose={closeEdit}>
        <ModalHeader>Editar Cabeçalho da Propriedade</ModalHeader>

        <ModalBody>
          <div className="space-y-3">
            <div>
              <Label value="Parcel Number *" />
              <TextInput
                value={editForm.parcel_number}
                onChange={(e) =>
                  setEditForm((s) => ({
                    ...s,
                    parcel_number: e.target.value,
                  }))
                }
                placeholder="Parcel Number"
              />
            </div>

            <div>
              <Label value="Owner" />
              <TextInput
                value={editForm.owner_name}
                onChange={(e) =>
                  setEditForm((s) => ({
                    ...s,
                    owner_name: e.target.value,
                  }))
                }
                placeholder="Owner"
              />
            </div>

            <div>
              <Label value="Address" />
              <TextInput
                value={editForm.property_address}
                onChange={(e) =>
                  setEditForm((s) => ({
                    ...s,
                    property_address: e.target.value,
                  }))
                }
                placeholder="Address"
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color="gray" onClick={closeEdit}>
            Cancelar
          </Button>

          <Button
            onClick={onUpdateHeader}
            className="text-slate-800"
            disabled={!editForm.parcel_number?.trim()}
          >
            Salvar
          </Button>
        </ModalFooter>
      </Modal>
      <PropertyCardModal
        open={cardOpen}
        onClose={closeCard}
        propertyId={cardPropertyId}
      />
    </div>
  );
}

